import React, { useState } from "react";
import {
  Box, Button, Typography,
  TextField, MenuItem, FormControl,
  Select, InputLabel, useTheme, Checkbox,
  ListItemText, Dialog, DialogActions,
  DialogContent, DialogTitle, Chip
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../../theme";
import { Link, useNavigate } from "react-router-dom";
import { useGetOrganizationsQuery, useGetMinimalOrganizationsQuery } from "../../../features/organizations/organizationsApiSlice";
import { useDiscreteMode } from "../global/DiscreteMode";
import PulseLoader from 'react-spinners/PulseLoader';
import Header from "../global/Header";
import { motion } from 'framer-motion';
import useAuth from "../../../hooks/useAuth";
import OrganizationForm from "../org_form";

const Organizations = () => {
  const { username, isManager, isAdmin } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { data: organizations, isLoading, isSuccess, isError, error } = useGetMinimalOrganizationsQuery('organizations', {
    pollingInterval: 15000,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: true
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState([]);
  const [open, setOpen] = useState(false);

  const { isDiscreteMode } = useDiscreteMode();

  const handleSearch = (e) => setSearchTerm(e.target.value);
  const handleFilterChange = (e) => setFilters(e.target.value);
  const handleRowClick = (param) => navigate(`/console/organization/${param.row.organization_id}`);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const renderCheck = (on, extra) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <span style={{ fontWeight: 'bold', fontSize: '1.5em', color: on ? 'green' : 'red' }}>
        {on ? '✓' : '✗'}
      </span>
      {extra}
    </Box>
  );

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      renderCell: (params) =>
        isDiscreteMode ? <Box width="140px" height="20px" bgcolor="white" /> : <span>{params.value}</span>,
    },
    {
      field: 'adc_product',
      headerName: 'ADC',
      flex: 0.55,
      sortable: true,
      valueGetter: (params) => {
        const products = params.row.products || [];
        return Array.isArray(products) && products.includes('ADC Resolution Process');
      },
      renderCell: (params) => {
        const hasAdc = Boolean(params.value);
        const hasUp = Boolean(params.row.adc_config?.up_id_output);

        return renderCheck(
          hasAdc,
          hasAdc && hasUp ? (
            <Chip
              size="small"
              label="+UP"
              sx={{
                height: 20,
                fontWeight: 700,
                bgcolor: 'rgba(144, 238, 144, 0.9)', // lightgreen-ish
                color: 'black',
              }}
            />
          ) : null
        );
      },
    },

    // APIs == products includes "APIs"
    {
      field: 'apis_product',
      headerName: 'APIs',
      flex: 0.55,
      sortable: true,
      valueGetter: (params) => {
        const products = params.row.products || [];
        return Array.isArray(products) && products.includes('APIs');
      },
      renderCell: (params) => renderCheck(Boolean(params.value)),
    },

    // Intent Feed == products includes "Intent Feed"
    {
      field: 'intent_feed_product',
      headerName: 'Intent Feed',
      flex: 0.75,
      sortable: true,
      valueGetter: (params) => {
        const products = params.row.products || [];
        return Array.isArray(products) && products.includes('Intent Feed');
      },
      renderCell: (params) => renderCheck(Boolean(params.value)),
    },

    // keep these if you still want them (optional):
    {
      field: 'real_time_resolution',
      headerName: 'Real Time',
      flex: 0.6,
      renderCell: (params) => renderCheck(Boolean(params.row.adc_config?.real_time_resolution)),
    },
    {
      field: 'beeswax_cookie_sync',
      headerName: 'Beeswax',
      flex: 0.6,
      renderCell: (params) => renderCheck(Boolean(params.value)),
    },
    {
      field: 'bito_hem_enabled',
      headerName: 'Bito HEM',
      flex: 0.6,
      renderCell: (params) => renderCheck(Boolean(params.value)),
    },

    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.7,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" justifyContent="flex-start" gap={1} p={1}>
          {isAdmin && (
            <Button
              variant="contained"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/console/organization/${params.row.organization_id}/edit`);
              }}
            >
              Edit
            </Button>
          )}
        </Box>
      ),
    },
  ];


  let content;

  if (isLoading) {
    content = <PulseLoader color={"#FFF"} />;
  }

  if (isError) {
    content = (
      <Box m="20px">
        <Box display="flex" justifyContent="end" mt="20px">
          <Link to="/console/organizations/new" color="secondary" variant="outlined">Onboard New Member</Link>
        </Box>
      </Box>
    );
  }

  if (isSuccess) {
    const { ids, entities } = organizations;
    const org_ents = organizations.entities;
    const orgs = Object.values(org_ents);

    console.log("Orgs: ", orgs);

    const filteredOrgs = orgs.filter((org) => {
      const searchTermLower = String(searchTerm || '').toLowerCase().trim();

      const nameMatch = String(org.name || '').toLowerCase().includes(searchTermLower);
      const pidMatch = String(org.pid || '').toLowerCase().includes(searchTermLower);
      const orgIdMatch = String(org.organization_id || '').toLowerCase().includes(searchTermLower);

      const searchMatch = nameMatch || pidMatch || orgIdMatch;

      if (filters.length === 0) return searchMatch;

      const filterFields = {
        'ADC': Array.isArray(org.products) && org.products.includes('ADC Resolution Process'),
        'APIs': Array.isArray(org.products) && org.products.includes('APIs'),
        'Intent Feed': Array.isArray(org.products) && org.products.includes('Intent Feed'),
        'Real Time Resolution': org.adc_config?.real_time_resolution,
        'Beeswax Cookie Sync': org.beeswax_cookie_sync,
        'Bito HEM Enabled': org.bito_hem_enabled,
      };

      return searchMatch && filters.every(filter => filterFields[filter]);
    });

    content = (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box m="20px">
          {isAdmin && (
            <Box display="flex" justifyContent="end" mt="20px">
              <Button color="secondary" variant="outlined" onClick={handleClickOpen}>
                Onboard New Member
              </Button>
            </Box>
          )}
          <Header title="Organizations" subtitle="Managing Organizations" />
          {/* New Search Bar and Filter Menu */}
          <Box mb="5px" mt="10px" display="flex" justifyContent="left" gap={2}>
            <TextField
              label="Search by Name, PID, or Org ID"
              variant="outlined"
              sx={{ width: '20%' }}  // set the width to 20% of the parent container
              onChange={handleSearch}
              value={searchTerm}
            />
            <FormControl variant="outlined" sx={{ width: '20%' }}>
              <InputLabel>Filter by Feature</InputLabel>
              <Select
                multiple
                value={filters}
                onChange={handleFilterChange}
                renderValue={(selected) => selected.join(', ')}
                label="Filter by Feature"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                <MenuItem value="ADC">
                  <Checkbox checked={filters.indexOf('ADC') > -1} />
                  <ListItemText primary="ADC" />
                </MenuItem>
                <MenuItem value="APIs">
                  <Checkbox checked={filters.indexOf('APIs') > -1} />
                  <ListItemText primary="APIs" />
                </MenuItem>
                <MenuItem value="Intent Feed">
                  <Checkbox checked={filters.indexOf('Intent Feed') > -1} />
                  <ListItemText primary="Intent Feed" />
                </MenuItem>
                <MenuItem value="UP ID Output">
                  <Checkbox checked={filters.indexOf('UP ID Output') > -1} />
                  <ListItemText primary="UP ID Output" />
                </MenuItem>
                <MenuItem value="Real Time Resolution">
                  <Checkbox checked={filters.indexOf('Real Time Resolution') > -1} />
                  <ListItemText primary="Real Time Resolution" />
                </MenuItem>
                <MenuItem value="Beeswax Cookie Sync">
                  <Checkbox checked={filters.indexOf('Beeswax Cookie Sync') > -1} />
                  <ListItemText primary="Beeswax Cookie Sync" />
                </MenuItem>
                <MenuItem value="Bito HEM Enabled">
                  <Checkbox checked={filters.indexOf('Bito HEM Enabled') > -1} />
                  <ListItemText primary="Bito HEM Enabled" />
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box
            m="40px 0 0 0"
            height="75vh"
            sx={{
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              "& .name-column--cell": {
                color: colors.blueAccent[600],
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: colors.grey[700],
                borderBottom: "none",
              },
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: colors.primary[400],
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "none",
                backgroundColor: colors.blueAccent[800],
              },
              "& .MuiCheckbox-root": {
                color: `${colors.blueAccent[200]} !important`,
              },
            }}
          >
            <DataGrid
              rows={filteredOrgs}
              columns={columns}
              onRowClick={handleRowClick}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'name', sort: 'asc' }],
                },
              }}
            />
          </Box>
        </Box>
      </motion.div>
    );
  }

  return (
    <>
      {content}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            borderRadius: 20
          }
        }}
      >
        <DialogTitle variant="h3">Onboard New Member Organization</DialogTitle>
        <DialogContent>
          <OrganizationForm />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined" sx={{ color: colors.redAccent[200], outlineColor: colors.redAccent[200] }} >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default Organizations;
